<template>
  <div class="chat-message-input d-flex p-3">
    <div class="mt-auto">
      <label
        :class="{ disabled: !selectedId }"
        class="mb-0 btn btn-white rounded-pill"
      >
        <input
          :disabled="!selectedId"
          class="visually-hidden"
          type="file"
          multiple
          @change="onFile"
        />
        <i class="icon-cloud-upload" />
      </label>
    </div>

    <div class="input-area flex-grow-1 mx-3 bg-white">
      <textarea
        v-model="message"
        ref="textarea"
        :disabled="!selectedId"
        rows="1"
        placeholder="Type a message..."
        @input="recalcTextareaHeight"
        @keydown.enter="sendMessage"
      />

      <div v-if="attachments.length" class="d-flex flex-wrap px-3 pb-1">
        <ChatAttachment
          v-for="(attachment, i) of attachments"
          v-bind="attachment"
          :key="attachment.filename"
          class="mr-2 mb-2"
          @remove="cancelAttachment(i)"
        />
      </div>
    </div>

    <div class="mt-auto">
      <b-btn
        v-if="message || attachments.length"
        :disabled="!selectedId || !canSend"
        variant="primary"
        pill
        @click="sendMessage"
      >
        <i class="icon-paper-plane" />
      </b-btn>
      <b-btn
        v-else
        :disabled="!selectedId"
        variant="primary"
        pill
        @click="sendThumbsUp"
      >
        <i class="icon-like" />
      </b-btn>
    </div>
  </div>
</template>

<script>
import API from "@/api";
import { mapActions, mapState } from "vuex";
import { getFiles } from "@/helpers/files";

import ChatAttachment from "./ChatAttachment";

export default {
  components: { ChatAttachment },

  data: () => ({
    attachments: [],
    message: ""
  }),

  computed: {
    ...mapState("chat", ["attachmentConfig", "selectedId"]),

    canSend() {
      if (this.attachments.length) {
        return this.attachments.every(a => a.progress === 1);
      }
      return this.message.trim().length > 0;
    }
  },

  watch: {
    selectedId() {
      this.attachments = [];
      this.message = "";
      this.$refs.textarea.focus();
    }
  },

  mounted() {
    this.recalcTextareaHeight();
  },

  methods: {
    ...mapActions("chat", ["updateLastRead"]),

    async addAttachments(files) {
      if (!files.length) return;

      for (const file of files) {
        const filename = file.name.replace(/[\\/]/g, "-");

        if (file.size > this.attachmentConfig.maxSize) {
          this.$notify({
            type: "info",
            text: `Skipping ${filename} as it is over 100MB`
          });
          continue;
        }

        const ext = filename.split(".").pop();
        if (this.attachmentConfig.blacklist.includes(ext)) {
          this.$notify({
            type: "info",
            text: `Skipping ${filename} as this file type is not allowed`
          });
          continue;
        }

        if (filename.length > this.attachmentConfig.maxNameLength) {
          this.$notify({
            type: "info",
            text: `Skipping ${filename} as the filename is too long`
          });
          continue;
        }

        const r = await API.addChatAttachment(this.selectedId, filename);

        if (!r.success) {
          this.$notify({
            type: "error",
            text: r.error.message
          });
          continue;
        }

        this.addAttachment(file, r.results);
      }
    },

    addAttachment(file, attachment) {
      const request = new XMLHttpRequest();
      const cancel = () => request.abort();

      const upload = {
        ...attachment,
        size: file.size,
        progress: 0,
        error: false,
        cancel
      };

      request.open("PUT", attachment.url);
      request.upload.addEventListener("progress", e => {
        upload.progress = e.loaded / e.total;
      });
      request.addEventListener("load", () => {
        if (request.status === 200) {
          upload.progress = 1;
        } else {
          upload.error = true;
        }
      });
      request.send(file);

      this.attachments.push(upload);
    },

    cancelAttachment(i) {
      const attachment = this.attachments[i];
      if (attachment.progress < 1) {
        attachment.cancel();
      }

      API.deleteChatAttachment(attachment.id);
      this.attachments.splice(i, 1);

      if (!this.attachments.length) {
        this.folder = null;
      }
    },

    async onDrop(e) {
      const files = await getFiles(e);
      this.addAttachments(files);
    },

    onFile(e) {
      const files = e.target.files;
      this.addAttachments(files);
      e.target.value = null;
    },

    async recalcTextareaHeight() {
      await this.$nextTick();

      const textarea = this.$refs.textarea;
      textarea.style.height = "auto";
      const { offsetHeight, clientHeight, scrollHeight } = textarea;
      const borderHeight = offsetHeight - clientHeight;
      textarea.style.height = scrollHeight + borderHeight * 3 + "px";
    },

    async sendMessage(e) {
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();

      if (!this.canSend) return;
      const message = this.message.trim();

      if (message.length) {
        const res = await API.sendChatMessage(this.selectedId, message);
        if (res.success) this.message = "";
        this.recalcTextareaHeight();
      }

      for (const attachment of this.attachments) {
        await API.sendChatMessage(this.selectedId, "", attachment.id);
      }
      this.attachments = [];

      await this.$nextTick();
      this.updateLastRead();
    },

    sendThumbsUp() {
      API.sendChatMessage(this.selectedId, "👍");
    }
  }
};
</script>

<style lang="scss">
.chat-message-input {
  .input-area {
    border-radius: 1em;
    overflow: hidden;
  }

  textarea {
    display: block;
    width: 100%;
    border: none;
    resize: none;
    padding: 0.5rem 1rem;
  }
}
</style>
